import { useEffect, useRef, useState } from 'react';
import { FloatingText, PassiveAbilityNotification } from '../components/BattleEffects';
import { BattleFxEntry } from '../components/BattleFx';
import { PassiveEvent, BattleUnitState } from '../types/battle';
import { getBattleTileSize } from '../utils/battleUtils';

export interface UnitReactionState {
  hitKey: number;
  lungeKey: number;
}

const SCREEN_SHAKE_DURATION_MS = 220;
// A death float snapshot outlives the CSS fall animation (500ms) by a small margin so the ghost
// doesn't pop out mid-animation.
const DEATH_GHOST_DURATION_MS = 600;

export function useBattleEffects() {
  const [floatingTexts, setFloatingTexts] = useState<FloatingText[]>([]);
  const [passiveNotifications, setPassiveNotifications] = useState<PassiveAbilityNotification[]>([]);
  const [fxEntries, setFxEntries] = useState<BattleFxEntry[]>([]);
  const [turnTransitionBanner, setTurnTransitionBanner] = useState<{ visible: boolean; unitName: string; isPlayer: boolean } | null>(null);
  // A queue rather than a single slot: two characters leveling up from the same kill (e.g. an
  // AOE finishing blow) would otherwise have the second trigger's setState silently clobber the
  // first's before it ever renders. The currently-shown banner is always the head of the queue.
  const [levelUpQueue, setLevelUpQueue] = useState<{ characterName: string; oldLevel: number; newLevel: number }[]>([]);

  // Hit-reaction/lunge sprite classes (GH-55). Keyed by unit id; each trigger bumps a counter
  // rather than toggling a boolean so BattleGridTiles can force-remount the sprite wrap via
  // `key` to restart the CSS animation cleanly even when hits land back-to-back on the same
  // unit (AOE, multi-hit combos) - no manual class add/remove timing to get wrong.
  const [unitReactions, setUnitReactions] = useState<Record<number, UnitReactionState>>({});

  // Short-lived snapshot of units that just died, so their death-fall animation has something to
  // render - the server deletes the real BattleUnit the moment defeat is applied, so by the time
  // setBattle reflects that, the unit is already gone from battle.units.
  const [dyingUnits, setDyingUnits] = useState<Record<number, BattleUnitState>>({});
  const dyingTimeoutsRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const [screenShaking, setScreenShaking] = useState(false);
  const screenShakeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Approximate defeat-screen stat, driven by the same death triggers as the fall animation -
  // covers attack-caused kills (the common case) but not e.g. skill/opportunity-attack kills,
  // which don't currently call triggerDeathAnimation. Good enough for a "nice to have" stat.
  const [enemiesDefeatedCount, setEnemiesDefeatedCount] = useState(0);

  const addFloatingText = (text: string, type: FloatingText['type'], x: number, y: number) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    setFloatingTexts(prev => [...prev, { id, text, type, x, y }]);
  };

  const addPassiveNotification = (
    abilityName: string,
    unitName: string,
    description: string,
    type: PassiveAbilityNotification['type']
  ) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    setPassiveNotifications(prev => [...prev, { id, abilityName, unitName, description, type }]);
  };

  const removeFloatingText = (id: string) => {
    setFloatingTexts(prev => prev.filter(text => text.id !== id));
  };

  const removePassiveNotification = (id: string) => {
    setPassiveNotifications(prev => prev.filter(n => n.id !== id));
  };

  const removeFxEntry = (id: string) => {
    setFxEntries(prev => prev.filter(e => e.id !== id));
  };

  /**
   * Spawn a projectile that travels from attacker tile to target tile,
   * followed by an impact burst at the target.
   */
  const spawnFx = (
    skillOrAction: string,
    fromPos: { x: number; y: number },
    toPos:   { x: number; y: number },
    tileSize: number = getBattleTileSize()
  ) => {
    const idBase = Date.now().toString() + Math.random().toString(36).substr(2, 5);
    const fromPx = fromPos.x * tileSize + tileSize / 2;
    const fromPy = fromPos.y * tileSize + tileSize / 2;

    const projectile: BattleFxEntry = {
      id: `${idBase}-proj`,
      type: 'projectile',
      skillOrAction,
      targetX: toPos.x,
      targetY: toPos.y,
      fromPixelX: fromPx,
      fromPixelY: fromPy,
      duration: 450,
    };
    setFxEntries(prev => [...prev, projectile]);

    // Impact fires after the projectile arrives
    setTimeout(() => {
      const impact: BattleFxEntry = {
        id: `${idBase}-impact`,
        type: 'impact',
        skillOrAction,
        targetX: toPos.x,
        targetY: toPos.y,
        duration: 300,
      };
      setFxEntries(prev => [...prev, impact]);
    }, 420);
  };

  const triggerHitReaction = (unitId: number) => {
    setUnitReactions(prev => {
      const existing = prev[unitId] ?? { hitKey: 0, lungeKey: 0 };
      return { ...prev, [unitId]: { ...existing, hitKey: existing.hitKey + 1 } };
    });
  };

  const triggerLunge = (unitId: number) => {
    setUnitReactions(prev => {
      const existing = prev[unitId] ?? { hitKey: 0, lungeKey: 0 };
      return { ...prev, [unitId]: { ...existing, lungeKey: existing.lungeKey + 1 } };
    });
  };

  const triggerDeathAnimation = (unit: BattleUnitState) => {
    if (!unit.isPlayerUnit) setEnemiesDefeatedCount(prev => prev + 1);
    setDyingUnits(prev => ({ ...prev, [unit.id]: unit }));
    if (dyingTimeoutsRef.current[unit.id]) clearTimeout(dyingTimeoutsRef.current[unit.id]);
    dyingTimeoutsRef.current[unit.id] = setTimeout(() => {
      setDyingUnits(prev => {
        const next = { ...prev };
        delete next[unit.id];
        return next;
      });
      delete dyingTimeoutsRef.current[unit.id];
    }, DEATH_GHOST_DURATION_MS);
  };

  // Double-rAF restart trick: dropping the class then re-adding it on a later frame guarantees
  // the browser paints the "off" state first, so a shake retriggered mid-animation (e.g. a crit
  // that kills) restarts cleanly instead of being a no-op class toggle.
  const triggerScreenShake = () => {
    if (screenShakeTimeoutRef.current) clearTimeout(screenShakeTimeoutRef.current);
    setScreenShaking(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setScreenShaking(true);
        screenShakeTimeoutRef.current = setTimeout(() => setScreenShaking(false), SCREEN_SHAKE_DURATION_MS);
      });
    });
  };

  const showTurnTransition = (unitName: string, isPlayer: boolean) => {
    setTurnTransitionBanner({ visible: true, unitName, isPlayer });
    setTimeout(() => setTurnTransitionBanner(null), 2000);
  };

  const triggerLevelUpCelebration = (characterName: string, oldLevel: number, newLevel: number) => {
    setLevelUpQueue(prev => [...prev, { characterName, oldLevel, newLevel }]);
  };

  // Dismiss the currently-shown banner (head of queue) after 3s, promoting the next one queued -
  // also used to hide it immediately if a victory/defeat screen is about to cover the same overlay.
  const levelUpTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (levelUpQueue.length === 0) return;
    levelUpTimeoutRef.current = setTimeout(() => setLevelUpQueue(prev => prev.slice(1)), 3000);
    return () => {
      if (levelUpTimeoutRef.current) clearTimeout(levelUpTimeoutRef.current);
    };
  }, [levelUpQueue]);

  const dismissLevelUpBanner = () => {
    if (levelUpTimeoutRef.current) clearTimeout(levelUpTimeoutRef.current);
    setLevelUpQueue([]);
  };

  const levelUpBanner = levelUpQueue[0] ?? null;

  const processPassiveEvents = (
    passiveEvents: PassiveEvent[],
    battleUnits: BattleUnitState[],
    addEnhancedBattleLog: (type: string, data: Record<string, unknown>) => void
  ) => {
    if (!passiveEvents || passiveEvents.length === 0) return;

    passiveEvents.forEach((event) => {
      addPassiveNotification(
        event.abilityName,
        event.unitName,
        event.description,
        event.type as PassiveAbilityNotification['type']
      );

      addEnhancedBattleLog('passive_ability', {
        unitName: event.unitName,
        abilityName: event.abilityName,
        description: event.description,
      });

      if (event.eventData) {
        let floatingText = '';
        let textType: FloatingText['type'] = 'passive';

        if (event.eventData.healing) {
          floatingText = `+${event.eventData.healing} HP`;
          textType = 'healing';
        } else if (event.eventData.damage) {
          floatingText = `${event.eventData.damage} DMG`;
          textType = 'damage';
        } else if (event.eventData.apGained) {
          floatingText = `+${event.eventData.apGained} AP`;
          textType = 'passive';
        }

        if (floatingText) {
          const unit = battleUnits.find(
            (u) => u.character?.name === event.unitName || u.enemyName === event.unitName
          );
          if (unit) {
            const x = unit.position.x * 80 + 40;
            const y = unit.position.y * 80 + 40;
            addFloatingText(floatingText, textType, x, y);
          }
        }
      }
    });
  };

  return {
    floatingTexts,
    passiveNotifications,
    fxEntries,
    turnTransitionBanner,
    levelUpBanner,
    unitReactions,
    dyingUnits,
    screenShaking,
    enemiesDefeatedCount,
    addFloatingText,
    addPassiveNotification,
    removeFloatingText,
    removePassiveNotification,
    removeFxEntry,
    spawnFx,
    showTurnTransition,
    triggerLevelUpCelebration,
    dismissLevelUpBanner,
    processPassiveEvents,
    triggerHitReaction,
    triggerLunge,
    triggerDeathAnimation,
    triggerScreenShake,
  };
}
